import { getChartConfiguration } from "./chart-configuration"
import { views } from "./helpers/views"
import { createSingleGradient } from "./helpers/gradient"
import { colors } from "./colors"

const getTemperatureChartConfiguration = (date, view, lang, type, fullscreen, debug = false, environment = "apps", timezone) => {
    let chartConfiguration = getChartConfiguration(date, view, lang, type, fullscreen, debug, environment, false, timezone)

    // all line graphs
    chartConfiguration.type = 'line'

    chartConfiguration.options.scales.yAxes[0].ticks.beginAtZero = false

    // all datasets
    for(const i in chartConfiguration.data.datasets) {
        chartConfiguration.data.datasets[i].datalabels = {
            display: false,
        }

        // If we only have 1 data point, we show it as a point.
        // A line graph with one point is not a line.
        chartConfiguration.data.datasets[i].pointRadius = (context) => {
            const { dataset } = context
            const datasetLength = dataset.data.filter(dataPoint => dataPoint.y !== null).length

            return datasetLength === 1 ? 4 : 0
        }

        chartConfiguration.data.datasets[i].fill = 'start'

        // The 'monotone' algorithm is more suited to y = f(x) datasets : it preserves monotonicity
        // (or piecewise monotonicity) of the dataset being interpolated, and ensures local extremums (if any)
        // stay at input data points.
        chartConfiguration.data.datasets[i].cubicInterpolationMode = 'monotone'
    }

    // [0] orange "mean"
    chartConfiguration.data.datasets[0] = {
        ...chartConfiguration.data.datasets[0],
        order: 1,
        borderColor: colors.temperature_mean.border
    }

    // [1] blue "min"
    chartConfiguration.data.datasets[1] = {
        ...chartConfiguration.data.datasets[1],
        borderDash: [1, 3],
        order: 0,
        borderColor: colors.temperature_min.border
    }

    // [2] pink "max"
    chartConfiguration.data.datasets[2] = {
        ...chartConfiguration.data.datasets[2],
        borderDash: [1, 3],
        order: 2,
        borderColor: colors.temperature_max.border
    }

    // override "beforeUpdate" of chart-configuration.js
    chartConfiguration.plugins[0].beforeUpdate = (chart, options) => {
        // Get the minium of the mimimums (dataset[1] contains all the aggregated min values)
        let min = chart.data.datasets[1].data.reduce((min, p) => min < p.y || p.y === null ? min : p.y, min) ?? 0

        // Get the maximum of the maximums (dataset[2] contains all the aggregated max values)
        let max = chart.data.datasets[2].data.reduce((max, p) => max > p.y || p.y === null ? max : p.y, max) ?? 0

        // Calculate max values for each dataset and store in dataset.max
        // This value is used later to construct the right gradient
        chart.data.datasets.forEach((dataset, index) => {
            if (dataset.data.length === 0) {
                return
            }

            dataset.max = dataset.data.reduce((max, p) => p.y !== null && p.y > max ? p.y : max, dataset.data[0]?.y)

            max = Math.max(max, dataset.max)
        })

        // Set suggested min and max for y-axis to nearest 1 or 5
        const yAxisNearest = views[chart.config.data.hw.view] === views.day ? 1 : 5

        const suggestedMin = Math.floor(min / yAxisNearest) * yAxisNearest
        const suggestedMax = Math.ceil(max / yAxisNearest) * yAxisNearest

        chart.options.scales.yAxes[0].ticks.suggestedMin = suggestedMin
        chart.options.scales.yAxes[0].ticks.suggestedMax = suggestedMax

        // set the "min" of all datasets to the absolute minimum value
        // this is needed to create the right gradient
        chart.data.datasets[0].min = suggestedMin
        chart.data.datasets[1].min = suggestedMin
        chart.data.datasets[2].min = suggestedMin
    }

    // Create background gradients
    chartConfiguration.plugins[0].afterLayout = (chart, options) => {
        // orange "mean temperature"
        chart.data.datasets[0].backgroundColor = createSingleGradient(
            chart,
            0,
            colors.temperature_mean.gradient_start,
            colors.temperature_mean.gradient_end,
        )

        if (views[chart.config.data.hw.view] === views.day) {
            console.log('day view, no background for min and max')

            return
        }

        // blue "min temperature"
        chart.data.datasets[1].backgroundColor = createSingleGradient(
            chart,
            1,
            colors.temperature_min.gradient_start,
            colors.temperature_min.gradient_end,
        )

        // pink "max temperature"
        chart.data.datasets[2].backgroundColor = createSingleGradient(
            chart,
            2,
            colors.temperature_max.gradient_start,
            colors.temperature_max.gradient_end,
        )
    }

    return chartConfiguration
}

export { getTemperatureChartConfiguration }
